/**
 * useMockSimulation hook
 *
 * Provides simulation API for QA testing without polluting window globals
 */

import { useRef, useCallback, Dispatch, SetStateAction } from 'react';
import type {
  VoiceDTMFSessionState,
  CardEntryState,
  CardType,
} from '@/types/voice-dtmf.types';
import { maskCardNumber, maskCVV, getCVVLength } from '@utils/cardUtils';

export interface SimulationAPI {
  simulateSecureModeOn: () => void;
  simulateSaveCard: () => void;
  simulateCustomCardNumber: (cardNumber: string) => void;
  simulateCustomCVV: (cvv: string) => void;
  simulateCustomExpiry: (expiry: string) => void;
}

interface UseMockSimulationProps {
  useMockData: boolean;
  mockAutoProgress: boolean;
  setSessionState: Dispatch<SetStateAction<VoiceDTMFSessionState>>;
  onSimulationReady?: (api: SimulationAPI) => void;
}

/**
 * Hook to provide simulation API for testing
 *
 * Instead of polluting window globals, this hook creates a clean API
 * and passes it to parent via callback.
 */
export const useMockSimulation = ({
  useMockData,
  mockAutoProgress,
  setSessionState,
  onSimulationReady,
}: UseMockSimulationProps) => {
  const simulationAPI = useRef<SimulationAPI>({
    simulateSecureModeOn: () => {
      setSessionState((prev) => ({
        ...prev,
        secureMode: { ...prev.secureMode, mode: 'on' },
      }));
    },

    simulateSaveCard: () => {
      setSessionState((prev) => ({
        ...prev,
        cardEntry: prev.cardEntry
          ? {
              ...prev.cardEntry,
              saveCard: !prev.cardEntry.saveCard,
            }
          : prev.cardEntry,
      }));
    },

    simulateCustomCardNumber: (cardNumber: string) => {
      const cleanCard = cardNumber.replace(/\s/g, '');

      // MOCK-ONLY: Simple card type detection for QA testing
      // Production uses Adyen BIN lookup service via backend (SSE FIELD_COMPLETE event)
      let cardType: CardType = 'UNKNOWN';
      if (cleanCard.startsWith('5')) cardType = 'MASTERCARD';
      else if (cleanCard.startsWith('34') || cleanCard.startsWith('37'))
        cardType = 'AMEX';
      else if (cleanCard.startsWith('4')) cardType = 'VISA';

      const masked = maskCardNumber(cleanCard);

      setSessionState((prev) => ({
        ...prev,
        cardEntry: prev.cardEntry
          ? ({
              ...prev.cardEntry,
              cardNumber: {
                value: masked,
                isComplete: true,
                digitsEntered: cleanCard.length,
                totalDigits: 16,
                _actualValue: cleanCard, // Store actual for test card logic
              } as CardEntryState['cardNumber'] & { _actualValue?: string },
              cardType,
            } as CardEntryState)
          : prev.cardEntry,
      }));
    },

    simulateCustomCVV: (cvv: string) => {
      const masked = maskCVV(cvv);

      setSessionState((prev) => ({
        ...prev,
        cardEntry: prev.cardEntry
          ? ({
              ...prev.cardEntry,
              cvv: {
                value: masked,
                isComplete: true,
                digitsEntered: cvv.length,
                totalDigits: getCVVLength(prev.cardEntry.cardType || 'VISA'),
                _actualValue: cvv, // Store actual for test card logic
              } as CardEntryState['cvv'] & { _actualValue?: string },
            } as CardEntryState)
          : prev.cardEntry,
      }));
    },

    simulateCustomExpiry: (expiry: string) => {
      setSessionState((prev) => ({
        ...prev,
        cardEntry: prev.cardEntry
          ? ({
              ...prev.cardEntry,
              expiryDate: expiry,
              _actualExpiry: expiry, // Store actual for test card logic
            } as CardEntryState & { _actualExpiry?: string })
          : prev.cardEntry,
      }));
    },
  });

  // Expose API to parent component via callback
  const exposeSimulationAPI = useCallback(() => {
    if (useMockData && !mockAutoProgress && onSimulationReady) {
      onSimulationReady(simulationAPI.current);
    }
  }, [useMockData, mockAutoProgress, onSimulationReady]);

  return {
    simulationAPI: simulationAPI.current,
    exposeSimulationAPI,
  };
};
